import { useState, useEffect } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  Button, Checkbox, CircularProgress, Alert, Paper, Badge, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
} from '@mui/material';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { formatPrset, currentPrset } from '../BookcardGenerator/hooks/useBookcardSet';
import { usePipelineStatus } from './hooks/usePipelineStatus';
import PipelineStatusRow, { statusSeverity } from './components/PipelineStatusRow';

function ProductionSetPipelineStatus() {
  const {
    prsets, presetsLoading, presetsError,
    products, productsLoading, productsError, loadProducts,
    recheckError, runPipeline,
    triggeredEans, rerunError, rerun,
    zipLoading, zipError, downloadZip,
    downloadingEans, downloadError, downloadOne,
    needsPickupCounts,
  } = usePipelineStatus();

  const [selectedPrset, setSelectedPrset] = useState('');
  // Flags default to showing the most-attention-needed rows first, same as
  // BookcardGenerator's Notes column; other columns default alphabetical/
  // ascending (or worst-first, for the status columns).
  const [sortBy, setSortBy] = useState('flags');
  const [sortDirection, setSortDirection] = useState('desc');
  const [downloadScope, setDownloadScope] = useState('all'); // 'all' | 'notAvailable'
  const [selectedEans, setSelectedEans] = useState(new Set());
  // zipLoading is shared by both ZIP buttons - this tracks which one to spin.
  const [zipSource, setZipSource] = useState(null); // 'scope' | 'selection'

  // Auto-select the current prset once the list has loaded — but only once,
  // so it doesn't fight with a manual selection made afterwards.
  useEffect(() => {
    if (prsets.length > 0 && !selectedPrset) {
      const preset = currentPrset(prsets);
      if (preset) setSelectedPrset(preset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prsets]);

  useEffect(() => {
    setSelectedEans(new Set());
    if (selectedPrset) loadProducts(selectedPrset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPrset]);

  // Titles with no term at all count as "not Lieferbar" - better to include
  // one too many in the delivery zip than silently drop one.
  const isLieferbar = (p) => (p.availabilityStatusTerm || '').trim().toLowerCase() === 'lieferbar';

  const isReady = (p) => p.xmp.status === 'success' && p.cover.status === 'success';

  const downloadableEans = products
    ? products
      .filter(isReady)
      .filter((p) => downloadScope === 'all' || !isLieferbar(p))
      .map((p) => p.ean)
    : [];
  const zipName = downloadScope === 'all' ? selectedPrset : `${selectedPrset}_nicht-lieferbar`;

  const readyEans = products ? products.filter(isReady).map((p) => p.ean) : [];
  // Intersect with readyEans: after a reload a selected row may no longer be ready.
  const selectedReadyEans = readyEans.filter((ean) => selectedEans.has(ean));
  const allSelected = readyEans.length > 0 && selectedReadyEans.length === readyEans.length;

  const toggleSelect = (ean) => {
    setSelectedEans((prev) => {
      const next = new Set(prev);
      if (next.has(ean)) next.delete(ean);
      else next.add(ean);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedEans(allSelected ? new Set() : new Set(readyEans));
  };

  const downloadScoped = () => {
    setZipSource('scope');
    downloadZip(downloadableEans, selectedPrset, zipName);
  };

  const downloadSelection = async () => {
    setZipSource('selection');
    const ok = await downloadZip(selectedReadyEans, selectedPrset, `${selectedPrset}_auswahl`);
    if (ok) setSelectedEans(new Set());
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection(column === 'flags' ? 'desc' : 'asc');
    }
  };

  const flagPriority = (p) => (p.isStale ? 1 : 0) + (p.needsPickup ? 1 : 0);

  const sortedProducts = products ? [...products].sort((a, b) => {
    let cmp = 0;
    switch (sortBy) {
      case 'title':
        cmp = (a.title || '').localeCompare(b.title || '');
        break;
      case 'ean':
        cmp = (a.ean || '').localeCompare(b.ean || '');
        break;
      case 'availability':
        cmp = (a.availabilityStatusTerm || '').localeCompare(b.availabilityStatusTerm || '');
        break;
      case 'xmp':
        cmp = statusSeverity(a.xmp.status) - statusSeverity(b.xmp.status);
        break;
      case 'cover':
        cmp = statusSeverity(a.cover.status) - statusSeverity(b.cover.status);
        break;
      case 'flags':
        cmp = flagPriority(a) - flagPriority(b);
        break;
      default:
        cmp = 0;
    }
    return sortDirection === 'asc' ? cmp : -cmp;
  }) : [];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom>
        Production Set - Pipeline Status
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <FormControl sx={{ minWidth: 240 }} disabled={presetsLoading}>
          <InputLabel id="prset-status-label">Production Set</InputLabel>
          <Select
            labelId="prset-status-label"
            label="Production Set"
            value={selectedPrset}
            onChange={(e) => setSelectedPrset(e.target.value)}
          >
            {prsets.map((p) => {
              const count = needsPickupCounts[p] || 0;
              return (
                <MenuItem key={p} value={p}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Box sx={{ flex: 1 }}>{formatPrset(p)}</Box>
                    {count > 0 && (
                      <Badge badgeContent={count} color="warning" max={99}>
                        <NewReleasesIcon fontSize="small" color="action" />
                      </Badge>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          onClick={() => runPipeline(selectedPrset)}
          disabled={!selectedPrset || productsLoading}
          startIcon={productsLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {productsLoading ? 'Running…' : 'Run Pipeline'}
        </Button>
        {products && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={downloadScope}
              onChange={(e, value) => { if (value !== null) setDownloadScope(value); }}
            >
              <ToggleButton value="all">Alle</ToggleButton>
              <ToggleButton value="notAvailable">Nicht Lieferbar</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              onClick={downloadScoped}
              disabled={downloadableEans.length === 0 || zipLoading}
              startIcon={zipLoading && zipSource === 'scope'
                ? <CircularProgress size={18} color="inherit" />
                : <FolderZipIcon />}
            >
              {`Download ZIP (${downloadableEans.length})`}
            </Button>
            <Button
              variant="outlined"
              onClick={downloadSelection}
              disabled={selectedReadyEans.length === 0 || zipLoading}
              startIcon={zipLoading && zipSource === 'selection'
                ? <CircularProgress size={18} color="inherit" />
                : <FolderZipIcon />}
            >
              {`Auswahl herunterladen (${selectedReadyEans.length})`}
            </Button>
          </Box>
        )}
      </Box>

      {presetsError && <Alert severity="error" sx={{ mb: 3 }}>{`Failed to load production sets: ${presetsError}`}</Alert>}
      {productsError && <Alert severity="error" sx={{ mb: 3 }}>{`Failed to load pipeline status: ${productsError}`}</Alert>}
      {rerunError && <Alert severity="error" sx={{ mb: 3 }}>{`Failed to trigger rerun: ${rerunError}`}</Alert>}
      {zipError && <Alert severity="error" sx={{ mb: 3 }}>{`Failed to download ZIP: ${zipError}`}</Alert>}
      {downloadError && <Alert severity="error" sx={{ mb: 3 }}>{`Failed to download PDF: ${downloadError}`}</Alert>}
      {recheckError && <Alert severity="error" sx={{ mb: 3 }}>{`Failed to trigger recheck: ${recheckError}`}</Alert>}

      {products && products.length === 0 && (
        <Alert severity="info">No products found in this set.</Alert>
      )}

      {products && products.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selectedReadyEans.length > 0 && !allSelected}
                    disabled={readyEans.length === 0}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'title'}
                    direction={sortBy === 'title' ? sortDirection : 'asc'}
                    onClick={() => handleSort('title')}
                  >
                    <strong>Title</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 130 }}>
                  <TableSortLabel
                    active={sortBy === 'ean'}
                    direction={sortBy === 'ean' ? sortDirection : 'asc'}
                    onClick={() => handleSort('ean')}
                  >
                    <strong>EAN</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 150 }}>
                  <TableSortLabel
                    active={sortBy === 'availability'}
                    direction={sortBy === 'availability' ? sortDirection : 'asc'}
                    onClick={() => handleSort('availability')}
                  >
                    <strong>Lieferstatus</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 170 }}>
                  <TableSortLabel
                    active={sortBy === 'xmp'}
                    direction={sortBy === 'xmp' ? sortDirection : 'asc'}
                    onClick={() => handleSort('xmp')}
                  >
                    <strong>XMP</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 170 }}>
                  <TableSortLabel
                    active={sortBy === 'cover'}
                    direction={sortBy === 'cover' ? sortDirection : 'asc'}
                    onClick={() => handleSort('cover')}
                  >
                    <strong>Cover</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 220 }}>
                  <TableSortLabel
                    active={sortBy === 'flags'}
                    direction={sortBy === 'flags' ? sortDirection : 'desc'}
                    onClick={() => handleSort('flags')}
                  >
                    <strong>Flags</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: 210 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProducts.map((product) => (
                <PipelineStatusRow
                  key={product.ean}
                  product={product}
                  justTriggered={triggeredEans.has(product.ean)}
                  onRerun={rerun}
                  onDownload={downloadOne}
                  downloading={downloadingEans.has(product.ean)}
                  selected={selectedEans.has(product.ean)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default ProductionSetPipelineStatus;
